// chatPageHelpers — ChatPage.js のコンポーネント外の純粋な定数/ヘルパを分離（挙動不変）。
// JSX/フックを含む定義は本体に残す（純粋なもののみ移動）。

import { appendVariant, buildChatAssetUrl, buildChatAssetUrlWithVariant } from '../../common/chat/composer/chatAssetUtils';
import { ADMIN_MESSAGE_FORMATS } from '../../../constants/messageFormats';
import { firstNonEmpty } from '../../common/chat/members/memberModalProfileUtils';

export const API_URL = process.env.REACT_APP_API_URL;
export const ARTIST_PROFILE_IMAGE_PATH = process.env.REACT_APP_ARTIST_PROFILE_IMAGE_PATH;
export const ADMIN_PROFILE_IMAGE_PATH = process.env.REACT_APP_ADMIN_PROFILE_IMAGE_PATH;
export const MESSAGE_IMAGE_PATH = process.env.REACT_APP_MESSAGE_IMAGE_PATH;
export const FLYER_IMAGE_PATH = process.env.REACT_APP_FLYER_IMAGE_PATH;
export const buildAssetUrl = (root, file) => buildChatAssetUrl({ apiUrl: API_URL, root, file });
export const buildFlyerUrl = (file, variant = 'card') =>
  buildChatAssetUrlWithVariant({ apiUrl: API_URL, root: FLYER_IMAGE_PATH, file, variant });
export const buildMessageImageUrl = (file, variant = 'proxy') =>
  buildChatAssetUrlWithVariant({ apiUrl: API_URL, root: MESSAGE_IMAGE_PATH, file, variant });
export const buildAdminProfileImageUrl = (file) =>
  file ? appendVariant(buildAssetUrl(ADMIN_PROFILE_IMAGE_PATH, file), 'proxy') : '';
export const buildAdminPersonalNotesListUrl = (artistId, offset = 0, limit = 20) =>
  `${API_URL}/api/admin/personal-chat-notes/${artistId}?offset=${offset}&limit=${limit}`;
export const ADMIN_ARTIST_ASSIGNEE_FILTER_STORAGE_KEY = 'admin:artist-chat:assignee-filter:v1';
export const ADMIN_ARTIST_PINNED_TARGETS_STORAGE_KEY = 'admin:artist-chat:pinned-targets:v1';
export const normalizeArtistId = (value) => (value == null ? '' : String(value));
export const normalizePinnedArtistIds = (value) => {
  const source = Array.isArray(value) ? value : [];
  const seen = new Set();
  return source
    .map((item) => normalizeArtistId(item).trim())
    .filter((id) => {
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
};
export const isSameArtistId = (left, right) => {
  if (left == null || right == null) return false;
  return normalizeArtistId(left) === normalizeArtistId(right);
};
export const normalizeMessageId = (value) => (value == null ? '' : String(value).trim());
export const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
export const getMessageDedupKey = (message) => {
  const messageId = normalizeMessageId(message?.id);
  if (messageId) return `id:${messageId}`;
  return [
    'fallback',
    message?.message_type || '',
    message?.message_format || '',
    message?.created_at || '',
    message?.admin_user_id || '',
    message?.artist_id || '',
    message?.image_id || '',
    message?.file_id || '',
    String(message?.content || '').trim(),
  ].join(':');
};
export const dedupeMessages = (list = []) => {
  const seen = new Set();
  const result = [];
  (Array.isArray(list) ? list : []).forEach((message) => {
    const key = getMessageDedupKey(message);
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push(message);
  });
  return result;
};
export const senderInitialFromName = (name) => String(name || '').trim().charAt(0).toUpperCase() || '?';
export const mergeMessagesByCreatedDesc = (current = [], additions = []) =>
  dedupeMessages(
    [...(Array.isArray(current) ? current : []), ...(Array.isArray(additions) ? additions : [])]
      .sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0)),
  );
export const UNSENDABLE_FORMATS = new Set([
  ADMIN_MESSAGE_FORMATS.TEXT,
  ADMIN_MESSAGE_FORMATS.IMAGE,
  ADMIN_MESSAGE_FORMATS.FILE,
]);
export const ADMIN_PERSONAL_UNREAD_FORMATS = new Set([
  ADMIN_MESSAGE_FORMATS.TEXT,
  ADMIN_MESSAGE_FORMATS.IMAGE,
  ADMIN_MESSAGE_FORMATS.FILE,
]);
export const getMessageFormat = (message = {}) =>
  String(message?.message_format || message?.messageFormat || '').trim().toLowerCase();
export const getMessageType = (message = {}) =>
  String(message?.message_type || message?.messageType || '').trim().toLowerCase();
export const isAdminPersonalUnreadMessage = (message = {}) =>
  getMessageType(message) === 'artist_message' && ADMIN_PERSONAL_UNREAD_FORMATS.has(getMessageFormat(message));
export const isAdminPersonalTargetMessageFormat = (message = {}) => {
  const format = getMessageFormat(message);
  return !format || ADMIN_PERSONAL_UNREAD_FORMATS.has(format);
};
export const isAdminPersonalUnreadPayload = (payload = {}) => {
  const type = getMessageType(payload);
  if (type && type !== 'artist_message') return false;
  const format = getMessageFormat(payload);
  return !format || ADMIN_PERSONAL_UNREAD_FORMATS.has(format);
};
export const isAdminPersonalOutgoingRead = (message = {}) => {
  if (message?.is_read === true || message?.isRead === true) return true;
  const readCount = Number(message?.read_count ?? message?.readCount ?? 0);
  return Number.isFinite(readCount) && readCount > 0;
};
export const buildRealtimePersonalTextMessage = (payload = {}) => {
  if (payload?.question) return null;
  const format = getMessageFormat(payload) || 'text';
  const messageId = normalizeMessageId(payload?.messageId || payload?.message_id);
  const content = firstNonEmpty(payload?.content, payload?.text, payload?.preview, payload?.messagePreview);
  if (format !== 'text' || !messageId || !content) return null;
  const senderType = String(payload?.senderType || payload?.sender_type || '').trim().toLowerCase();
  const messageType =
    getMessageType(payload) ||
    (senderType === 'admin' ? 'admin_message' : 'artist_message');
  const adminUserId =
    payload?.senderAdminId ||
    payload?.sender_admin_id ||
    payload?.adminUserId ||
    payload?.admin_user_id ||
    (senderType === 'admin' ? payload?.senderId || payload?.sender_id : null);
  return {
    id: messageId,
    artist_id: payload?.artistId || payload?.artist_id || null,
    message_type: messageType,
    message_format: 'text',
    content,
    admin_user_id: messageType === 'admin_message' ? adminUserId || null : null,
    created_at: new Date().toISOString(),
    is_read: false,
    reactions: [],
  };
};
